import type { Vector2 } from "./geometry";
import { distance, distanceToSegment, projectOntoSegment } from "./geometry";
import { MiterSourceEdge } from "./MiterSourceEdge";
import { MiterSourceNode } from "./MiterSourceNode";

export interface MiterEdgeHit {
  edge: MiterSourceEdge;
  projection: Vector2;
}

export class MiterSourceGraph {
  readonly nodes: MiterSourceNode[] = [];
  edges: MiterSourceEdge[] = [];
  readonly defaultWidth: number = 30;

  // Wedge tags, keyed by source node. A tag (node, edge) identifies
  // the wedge entered by crossing `edge` while spinning CCW around
  // `node`. Marks a wedge vertex that should be split into two
  // skeleton vertices connected by a zero-motion chord, each bound
  // to only one adjacent wavefront. Stored here so it survives
  // skeleton rebuilds.
  readonly wedgeTags: Map<MiterSourceNode, Set<MiterSourceEdge>> = new Map();

  addNode(position: Vector2): MiterSourceNode {
    const node = new MiterSourceNode(position);
    this.nodes.push(node);
    return node;
  }

  connect(a: MiterSourceNode, b: MiterSourceNode): MiterSourceEdge | null {
    if (a === b) {
      return null;
    }

    const existing = this.findEdge(a, b);

    if (existing) {
      return existing;
    }

    const edge = new MiterSourceEdge(a, b, this.defaultWidth);
    this.edges.push(edge);
    a.connections.push(b);
    b.connections.push(a);
    return edge;
  }

  disconnect(a: MiterSourceNode, b: MiterSourceNode): void {
    const edge = this.findEdge(a, b);

    if (edge) {
      this.clearWedgeTagsForEdge(edge);
    }

    this.edges = this.edges.filter((e) => !((e.a === a && e.b === b) || (e.a === b && e.b === a)));
    a.connections = a.connections.filter((n) => n !== b);
    b.connections = b.connections.filter((n) => n !== a);
  }

  addWedgeTag(node: MiterSourceNode, edge: MiterSourceEdge): void {
    let set = this.wedgeTags.get(node);

    if (!set) {
      set = new Set();
      this.wedgeTags.set(node, set);
    }

    set.add(edge);
  }

  hasWedgeTag(node: MiterSourceNode, edge: MiterSourceEdge): boolean {
    return this.wedgeTags.get(node)?.has(edge) ?? false;
  }

  removeWedgeTag(node: MiterSourceNode, edge: MiterSourceEdge): void {
    const set = this.wedgeTags.get(node);

    if (!set) {
      return;
    }

    set.delete(edge);

    if (set.size === 0) {
      this.wedgeTags.delete(node);
    }
  }

  private clearWedgeTagsForEdge(edge: MiterSourceEdge): void {
    for (const [node, set] of this.wedgeTags) {
      set.delete(edge);

      if (set.size === 0) {
        this.wedgeTags.delete(node);
      }
    }
  }

  findEdge(a: MiterSourceNode, b: MiterSourceNode): MiterSourceEdge | null {
    for (const e of this.edges) {
      if ((e.a === a && e.b === b) || (e.a === b && e.b === a)) {
        return e;
      }
    }
    return null;
  }

  nearestNode(p: Vector2, tolerance: number): MiterSourceNode | null {
    let best: MiterSourceNode | null = null;
    let bestDist = tolerance;
    for (const node of this.nodes) {
      const d = distance(p, node.position);
      if (d <= bestDist) {
        bestDist = d;
        best = node;
      }
    }
    return best;
  }

  nearestEdge(p: Vector2, tolerance: number): MiterEdgeHit | null {
    let best: MiterEdgeHit | null = null;
    let bestDist = tolerance;

    for (const edge of this.edges) {
      const d = distanceToSegment(p, edge.a.position, edge.b.position);

      if (d <= bestDist) {
        bestDist = d;
        best = {
          edge,
          projection: projectOntoSegment(p, edge.a.position, edge.b.position),
        };
      }
    }

    return best;
  }

  splitEdge(a: MiterSourceNode, b: MiterSourceNode, at: Vector2): MiterSourceNode {
    // Preserve the original edge's (stored) orientation and
    // per-side widths on both new halves. disconnect() removes
    // the original edge before we build replacements.
    const original = this.findEdge(a, b);
    const origA = original ? original.a : a;
    const origB = original ? original.b : b;
    const leftWidth = original ? original.leftWidth : this.defaultWidth;
    const rightWidth = original ? original.rightWidth : this.defaultWidth;

    this.disconnect(a, b);

    const mid = this.addNode(at);

    const first = new MiterSourceEdge(origA, mid, 0);
    first.leftWidth = leftWidth;
    first.rightWidth = rightWidth;

    const second = new MiterSourceEdge(mid, origB, 0);
    second.leftWidth = leftWidth;
    second.rightWidth = rightWidth;

    this.edges.push(first, second);
    origA.connections.push(mid);
    mid.connections.push(origA, origB);
    origB.connections.push(mid);

    return mid;
  }
}
